/* ───── UI-free consult client ────────────────────────────────────
 * The Consultation panel's request/reply logic, with no DOM or React:
 * posting a turn, holding the Response ID chain (ADR-0001), and mapping
 * every failure to the fallback bubble. The path is a same-origin
 * constant: the upstream base URL, key and model live only in the
 * /api/consult function's environment, so nothing about the upstream is
 * configurable — or visible — here.
 * ────────────────────────────────────────────────────────────── */

export const NX_CONSULT_PATH = "/api/consult";
export const NX_CONSULT_MESSAGES_PATH = "/api/consult/messages";
export const NX_CONSULT_POINTER_KEY = "nx.consult.pointer";
export const NX_CONSULT_UNREAD_KEY = "nx.consult.unread";
export const NX_CONSULT_TTL_MS = 30 * 60 * 1000;

const CONSULTATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CONSULTATION_ACTIVITIES = new Set(["open", "minimize", "visitor-submit", "consultant-turn"]);

function storageOrNull(storage) {
  if (storage) return storage;
  try { return typeof window === "undefined" ? null : window.localStorage; } catch { return null; }
}

export function readConsultationPointer({ storage, now = Date.now() } = {}) {
  const target = storageOrNull(storage);
  try {
    const pointer = JSON.parse(target?.getItem(NX_CONSULT_POINTER_KEY) || "null");
    if (!CONSULTATION_ID.test(pointer?.consultationId || "") ||
        !Number.isFinite(pointer?.expiresAt) || pointer.expiresAt <= now ||
        (pointer.responseId !== null && typeof pointer.responseId !== "string")) {
      target?.removeItem(NX_CONSULT_POINTER_KEY);
      return null;
    }
    return { consultationId: pointer.consultationId, expiresAt: pointer.expiresAt, responseId: pointer.responseId };
  } catch {
    try { target?.removeItem(NX_CONSULT_POINTER_KEY); } catch {}
    return null;
  }
}

function writeConsultationPointer(pointer, storage) {
  try { storageOrNull(storage)?.setItem(NX_CONSULT_POINTER_KEY, JSON.stringify(pointer)); } catch {}
}

function messageText(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content.map((part) => typeof part === "string" ? part :
    (typeof part?.text === "string" ? part.text : "")).filter(Boolean).join("\n");
}

function messageMedia(content) {
  if (!Array.isArray(content)) return [];
  return content.flatMap((part) => {
    const url = typeof part?.image_url === "string" ? part.image_url : part?.image_url?.url ||
      (typeof part?.video_url === "string" ? part.video_url : part?.video_url?.url);
    if (!url || !/^https?:\/\//i.test(url)) return [];
    return [{ type: String(part.type || "").includes("video") ? "video" : "image", url, alt: part.alt || "Consultant reply media" }];
  });
}

// The upstream transcript stores `ask_user` as a generic `tool_call` wrapper —
// `{ name: "tool_call", arguments: "{\"name\":\"ask_user\",\"arguments\":{…}}" }` —
// while the live stream emits `ask_user` directly. Unwrap so both parse alike.
function unwrapToolCall(fn) {
  const passthrough = { name: fn.name, arguments: fn.arguments };
  if (fn.name !== "tool_call") return passthrough;
  try {
    const inner = JSON.parse(fn.arguments);
    if (typeof inner?.name !== "string") return passthrough;
    const args = inner.arguments;
    return { name: inner.name, arguments: typeof args === "string" ? args : JSON.stringify(args ?? null) };
  } catch {
    return passthrough;
  }
}

export function normalizeConsultationMessages(payload) {
  const source = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.messages) ? payload.messages : [];
  const messages = [];
  let focus = null;
  const formsByCall = new Map();
  for (const item of source) {
    const metadata = item?.metadata || {};
    const representedFocus = metadata.focusService || metadata.focus_service || item?.focusService || item?.focus_service;
    if (typeof representedFocus === "string") focus = representedFocus;

    const candidates = [item, ...(Array.isArray(item?.tool_calls) ? item.tool_calls.map((call) => call.function ? {
      type: "function_call", call_id: call.id, ...unwrapToolCall(call.function),
    } : call) : [])];
    for (const candidate of candidates) {
      const form = parseConsultantForm(candidate);
      if (form) {
        formsByCall.set(form.callId, form);
        messages.push({ role: "assistant", content: form.message || form.title || "Please provide a few details.", form });
      }
      const completedCall = candidate?.call_id || candidate?.tool_call_id;
      if ((candidate?.type === "function_call_output" || item?.role === "tool") && formsByCall.has(completedCall)) {
        formsByCall.get(completedCall).answered = "submitted";
      }
    }

    // The Consultant's text refers to its form as "above", so the form comes first.
    if (item?.role === "user" || item?.role === "assistant") {
      let content = messageText(item.content);
      const focusMatch = item.role === "user" && content.match(/^\[service focus: ([^\]]+)]\n?/);
      if (focusMatch) { focus = focusMatch[1]; content = content.slice(focusMatch[0].length); }
      const media = messageMedia(item.content);
      if (content || media.length) messages.push({ role: item.role, content, ...(media.length ? { media } : {}) });
    }
  }
  return { messages, focus };
}

// What the Consultant says when the upstream is down — the conversion path
// stays alive: the Visitor can still choose a solution or send a requirement.
export const NX_CONSULT_FALLBACK =
  "I'm having trouble connecting right now, but you can still choose a solution below or send your requirement to our team.";

export const NX_CONSULT_REFUSAL =
  "You've sent several messages in a short time. Please wait a minute, then try again.";

export function consultationReplyMetadata(result, input, focusService) {
  return {
    ...(result.fallback ? { fallback: true } : {}),
    ...(result.retryable ? { retry: { input, focusService } } : {}),
  };
}

function parseConsultantForm(item) {
  if (item?.type !== "function_call" || item?.name !== "ask_user" || typeof item.call_id !== "string") return null;
  let args;
  try { args = JSON.parse(item.arguments); } catch { return null; }
  if (!Array.isArray(args?.fields)) return null;
  const fields = args.fields.filter((field) =>
    typeof field?.key === "string" &&
    typeof field?.label === "string" &&
    ["text", "select", "boolean"].includes(field?.type),
  );
  if (!fields.length) return null;
  return {
    callId: item.call_id,
    ...(typeof args.title === "string" ? { title: args.title } : {}),
    ...(typeof args.message === "string" ? { message: args.message } : {}),
    ...(typeof args.submitLabel === "string" ? { submitLabel: args.submitLabel } : {}),
    fields,
  };
}

export function serializeConsultantFormAnswers(form, answers) {
  const values = form.fields.map((field) => {
    const value = field.type === "boolean" ? (answers[field.key] ? "Yes" : "No") : answers[field.key];
    return `${field.key} = ${value ?? ""}`;
  });
  return `The user answered the form: ${values.join(", ")}`;
}

export function createConsultClient({ fetchImpl, storage, now = () => Date.now(), randomUUID } = {}) {
  const client = {
    // Injectable browser boundaries keep persistence testable in Node.
    fetchImpl,
    storage,
    now,
    randomUUID,

    consultationId: null,
    expiresAt: null,
    responseId: null,

    expired: false,
    inFlightTurns: 0,

    loadPointer() {
      const pointer = readConsultationPointer({ storage: client.storage, now: client.now() });
      client.consultationId = pointer?.consultationId || null;
      client.expiresAt = pointer?.expiresAt || null;
      client.responseId = pointer?.responseId || null;
      client.expired = false;
      return pointer;
    },

    adoptPointer(value) {
      let pointer = null;
      try {
        const candidate = JSON.parse(value || "null");
        if (CONSULTATION_ID.test(candidate?.consultationId || "") &&
            Number.isFinite(candidate?.expiresAt) && candidate.expiresAt > client.now() &&
            (candidate.responseId === null || typeof candidate.responseId === "string")) {
          pointer = {
            consultationId: candidate.consultationId,
            expiresAt: candidate.expiresAt,
            responseId: candidate.responseId,
          };
        }
      } catch {}
      client.consultationId = pointer?.consultationId || null;
      client.expiresAt = pointer?.expiresAt || null;
      client.responseId = pointer?.responseId || null;
      client.expired = false;
      return pointer;
    },

    persistPointer() {
      if (!client.consultationId) return;
      client.expiresAt = client.now() + NX_CONSULT_TTL_MS;
      writeConsultationPointer({
        consultationId: client.consultationId,
        expiresAt: client.expiresAt,
        responseId: client.responseId,
      }, client.storage);
    },

    recordActivity(activity) {
      if (!CONSULTATION_ACTIVITIES.has(activity) || !client.consultationId || client.expireIfInactive()) return false;
      client.persistPointer();
      return true;
    },

    expireIfInactive() {
      if (client.inFlightTurns > 0 || !client.consultationId || !Number.isFinite(client.expiresAt) || client.expiresAt > client.now()) return false;
      client.startNewConsultation({ expired: true });
      return true;
    },

    ensureConsultation() {
      if (client.expireIfInactive() || client.expired) return null;
      if (!client.consultationId) {
        const uuid = client.randomUUID || globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
        if (!uuid) throw new Error("secure random UUID unavailable");
        const consultationId = uuid();
        if (!CONSULTATION_ID.test(consultationId)) throw new Error("invalid generated Consultation ID");
        client.consultationId = consultationId;
        client.persistPointer();
      }
      return client.consultationId;
    },

    startNewConsultation({ expired = false } = {}) {
      client.consultationId = null;
      client.expiresAt = null;
      client.responseId = null;
      client.expired = expired;
      try { storageOrNull(client.storage)?.removeItem(NX_CONSULT_POINTER_KEY); } catch {}
    },

    async restoreConsultation(pointer = client.loadPointer(), { clearMissing = true } = {}) {
      if (!pointer) return null;
      try {
        const doFetch = client.fetchImpl || fetch;
        const response = await doFetch(`${NX_CONSULT_MESSAGES_PATH}?consultationId=${encodeURIComponent(pointer.consultationId)}`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          if (response.status === 404) {
            if (clearMissing && client.consultationId === pointer.consultationId &&
                client.expiresAt === pointer.expiresAt &&
                client.responseId === pointer.responseId) {
              client.startNewConsultation();
            }
            return { status: "missing" };
          }
          return { status: "temporary-error" };
        }
        const transcript = normalizeConsultationMessages(await response.json());
        return { ...transcript, responseId: pointer.responseId, status: "restored" };
      } catch {
        return { status: "temporary-error" };
      }
    },

    // One Consultation turn. Never throws: any failure becomes the fallback
    // bubble, and only a successful turn advances the chain — a failure
    // leaves the previous id in place, so the next turn resumes the
    // Consultation instead of silently starting a new one.
    async sendTurn({ input, focusService, resumeCallId, onToken, onForm, signal }) {
      let reply = "";
      let streamedResponseId = null;
      let acceptedTurn = false;
      const pendingForms = [];
      try {
        const doFetch = client.fetchImpl || fetch;
        const consultationId = client.ensureConsultation();
        if (!consultationId) return { reply: NX_CONSULT_FALLBACK, fallback: true, expired: true };
        client.inFlightTurns += 1;
        acceptedTurn = true;
        const streaming = typeof onToken === "function";
        const r = await doFetch(NX_CONSULT_PATH, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Hermes-Session-Id": consultationId,
            ...(streaming ? { Accept: "text/event-stream" } : {}),
          },
          body: JSON.stringify({
            input,
            focusService,
            previousResponseId: client.responseId,
            ...(resumeCallId ? { resumeCallId } : {}),
          }),
          signal,
        });
        if (!r.ok) {
          if (r.status === 429) {
            const error = await r.json().catch(() => null);
            if (error?.error === "rate_limit") {
              return { reply: NX_CONSULT_REFUSAL, fallback: false };
            }
          }
          throw new Error("consult " + r.status);
        }
        if (streaming) {
          const reader = r.body?.getReader();
          if (!reader) throw new Error("consult empty stream");
          const decoder = new TextDecoder();
          let pending = "";
          const consume = (frame) => {
            for (const line of frame.split(/\r\n|\r|\n/)) {
              if (!line.startsWith("data:")) continue;
              const data = line.slice(5).trim();
              if (!data || data === "[DONE]") continue;
              let event;
              try { event = JSON.parse(data); } catch { continue; }
              if (event?.type === "response.created" && event?.response?.id) {
                streamedResponseId = event.response.id;
              }
              if (event?.type === "response.output_text.delta" && typeof event.delta === "string") {
                reply += event.delta;
                onToken(event.delta);
              }
              if (event?.type === "response.output_item.done" && typeof onForm === "function") {
                const form = parseConsultantForm(event.item);
                if (form) pendingForms.push(form);
              }
            }
          };
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            pending += decoder.decode(value, { stream: true });
            let boundary;
            while ((boundary = pending.search(/\r?\n\r?\n/)) !== -1) {
              const separator = pending.match(/\r?\n\r?\n/)?.[0] || "\n\n";
              consume(pending.slice(0, boundary));
              pending = pending.slice(boundary + separator.length);
            }
          }
          pending += decoder.decode();
          if (pending.trim()) consume(pending);
          if (!reply && !pendingForms.length) throw new Error("consult empty reply");
          if (client.consultationId === consultationId) {
            if (streamedResponseId) client.responseId = streamedResponseId;
            client.recordActivity("visitor-submit");
            client.recordActivity("consultant-turn");
          }
          for (const form of pendingForms) onForm(form);
          return { reply, fallback: false };
        }
        const j = await r.json();
        if (!j.reply) throw new Error("consult empty reply");
        if (client.consultationId === consultationId) {
          if (j.responseId) client.responseId = j.responseId;
          client.recordActivity("visitor-submit");
          client.recordActivity("consultant-turn");
        }
        return { reply: j.reply, fallback: false };
      } catch (error) {
        if (signal?.aborted || error?.name === "AbortError") return { reply, aborted: true };
        if (reply) return { reply, fallback: false, retryable: true };
        return { reply: NX_CONSULT_FALLBACK, fallback: true, retryable: true };
      } finally {
        if (acceptedTurn) client.inFlightTurns -= 1;
      }
    },

    // Phase-1 conversion seam. Posts the consultation brief + contact details
    // to the backend, which relays a summary into the Slack "AI Consultant"
    // group for the NEXT INNOVATIONS team to follow up. Swap briefEndpoint to
    // go live; until then it resolves successfully so the flow is fully
    // demonstrable.
    briefEndpoint: null, // e.g. "https://api.next-innovations.ltd/consult/brief"
    async sendBrief(payload) {
      if (client.briefEndpoint) {
        const doFetch = client.fetchImpl || fetch;
        const r = await doFetch(client.briefEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error("brief endpoint " + r.status);
        return r.json();
      }
      return new Promise((res) => setTimeout(() => res({ ok: true, channel: "#ai-consultant" }), 950));
    },
  };
  return client;
}

// The app's single client — one Consultation per page load.
export const NX_API = createConsultClient();
